import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { isApiErrorMessage } from 'src/api/utils/is-api-error-message';
import { ApiError } from 'src/types/interfaces/api-error';
import { delayedCallbackExecution } from 'src/utils/delayed-callback-execution';
import { spacesApiErrorsTranslations } from '../constants/api-errors-translations';
import { blockSpace } from '../lib/spaces';
import { spacesKeys } from '../query-keys/spaces';
import { ApiErrorMessage } from '../types/api-error-message';
import { BlockSpacePayload } from '../types/payload/block-space.payload';

export const useBlockSpace = (onSuccess?: () => void) => {
  const { t } = useTranslation(['spaces', 'common']);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      spaceToken,
      payload,
    }: {
      spaceToken: string;
      payload: BlockSpacePayload;
    }) => blockSpace(spaceToken, payload),
    onSuccess: () => {
      delayedCallbackExecution(1000, () => {
        queryClient.invalidateQueries({
          queryKey: spacesKeys.lists(),
        });
      });

      toast.success(t('spaces:common.blockSpaceDialog.successMessage'));

      onSuccess?.();
    },
    onError: (error: ApiError) => {
      const errorMessage = error?.response?.data?.message;

      if (
        isApiErrorMessage<ApiErrorMessage>(
          errorMessage,
          spacesApiErrorsTranslations,
        )
      ) {
        toast.error(t(spacesApiErrorsTranslations[errorMessage]));

        return;
      }

      toast.error(errorMessage || t('common:errorMessages.somethingWentWrong'));
    },
  });
};
